import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Search, 
  Building2, 
  Phone, 
  Mail, 
  MapPin, 
  TrendingUp, 
  AlertTriangle,
  Star,
  Grid3X3,
  List,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Target,
  Copy,
  Merge,
  X,
  Check,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Clock,
  FileCheck,
  Plus,
  Trash2,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { FilterPanel } from '@/components/FilterPanel';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';

const KRALJIC_COLORS = {
  Strategic: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  Leverage: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  Bottleneck: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  'Non-Critical': { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' }
};

const VERIFICATION_CONFIG = {
  unverified: { label: 'Unverified', icon: ShieldAlert, bg: 'bg-gray-100 text-gray-600', ring: 'ring-gray-300' },
  pending: { label: 'Pending', icon: Clock, bg: 'bg-amber-100 text-amber-700', ring: 'ring-amber-300' },
  verified: { label: 'Verified', icon: ShieldCheck, bg: 'bg-emerald-100 text-emerald-700', ring: 'ring-emerald-300' },
  rejected: { label: 'Rejected', icon: ShieldX, bg: 'bg-red-100 text-red-700', ring: 'ring-red-300' },
  expired: { label: 'Expired', icon: ShieldAlert, bg: 'bg-orange-100 text-orange-700', ring: 'ring-orange-300' },
};

const getKraljicBadge = (status) => {
  const colors = KRALJIC_COLORS[status] || KRALJIC_COLORS['Non-Critical'];
  return colors;
};

function DuplicateGroup({ group, index, onNormalize, normalizing, currencySymbol }) {
  const [masterId, setMasterId] = useState(null);

  const suppliers = group.suppliers || [];
  const matchLabel = group.match_type === 'exact_normalized' ? 'Exact' : `Fuzzy (${Math.round(group.confidence * 100)}%)`;

  return (
    <Card data-testid={`duplicate-group-${index}`}>
      <CardContent className="pt-4 pb-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{matchLabel}</Badge>
            <span className="text-xs text-muted-foreground">
              {suppliers.length} suppliers &middot; {currencySymbol}{group.combined_spend?.toLocaleString()}
            </span>
          </div>
          <Button
            size="sm"
            disabled={!masterId || normalizing === masterId}
            onClick={() => {
              const mergeIds = suppliers.filter(s => s.id !== masterId).map(s => s.id);
              onNormalize(masterId, mergeIds);
            }}
            data-testid={`normalize-btn-${index}`}
            className="h-7 text-xs"
          >
            {normalizing === masterId ? 'Merging...' : 'Normalize'}
          </Button>
        </div>
        <div className="space-y-1.5">
          {suppliers.map((s) => (
            <div
              key={s.id}
              className={`flex items-center justify-between p-2 rounded-md border text-sm cursor-pointer transition-colors ${
                masterId === s.id
                  ? 'border-primary bg-primary/5'
                  : 'hover:bg-muted/50'
              }`}
              onClick={() => setMasterId(s.id)}
              data-testid={`dup-supplier-${s.id}`}
            >
              <div className="flex items-center gap-2">
                {masterId === s.id ? (
                  <Check className="w-4 h-4 text-primary shrink-0" />
                ) : (
                  <div className="w-4 h-4 rounded-full border border-muted-foreground/30 shrink-0" />
                )}
                <span className={masterId === s.id ? 'font-medium' : ''}>{s.name}</span>
                {masterId === s.id && (
                  <Badge className="text-[10px] h-4 px-1">Master</Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>{s.category || '—'}</span>
                <span>{currencySymbol}{(s.total_spend || 0).toLocaleString()}</span>
                <span>{s.transaction_count || 0} txns</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Click a supplier to set it as master. All transactions from other suppliers will be reassigned to the master record.
        </p>
      </CardContent>
    </Card>
  );
}

export default function SupplierDirectory() {
  const { symbol: cs } = useCurrency();
  const { filters: dataFilters, setFilters: setDataFilters } = useGlobalFilters();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [kraljicFilter, setKraljicFilter] = useState('all');
  const [sortBy, setSortBy] = useState('total_spend');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [duplicates, setDuplicates] = useState(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [normalizing, setNormalizing] = useState(null);
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [verificationModalSupplier, setVerificationModalSupplier] = useState(null);
  const [certModalSupplier, setCertModalSupplier] = useState(null);
  const [certs, setCerts] = useState([]);
  const [verifyStatus, setVerifyStatus] = useState('');
  const [verifyNotes, setVerifyNotes] = useState('');
  const [newCert, setNewCert] = useState({ name: '', issuer: '', issue_date: '', expiry_date: '' });
  const [verifyLoading, setVerifyLoading] = useState(false);

  useEffect(() => {
    fetchSuppliers();
    fetchDuplicates();
  }, [dataFilters.category_l2, dataFilters.category_l3, dataFilters.addressability]);

  const fetchSuppliers = async () => {
    try {
      const params = new URLSearchParams();
      if (dataFilters.category_l2) params.append('category_l2', dataFilters.category_l2);
      if (dataFilters.category_l3) params.append('category_l3', dataFilters.category_l3);
      if (dataFilters.addressability) params.append('addressability', dataFilters.addressability);
      const qs = params.toString();
      const response = await api.get(`/suppliers${qs ? `?${qs}` : ''}`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchDuplicates = async () => {
    try {
      const res = await api.get('/suppliers/duplicates');
      setDuplicates(res.data);
    } catch {
      console.error('Failed to detect duplicates');
    }
  };

  const normalizeGroup = async (masterId, mergeIds) => {
    setNormalizing(masterId);
    try {
      await api.post('/suppliers/normalize', { master_id: masterId, merge_ids: mergeIds });
      toast.success('Suppliers normalized successfully');
      await fetchSuppliers();
      await fetchDuplicates();
    } catch {
      toast.error('Failed to normalize suppliers');
    } finally {
      setNormalizing(null);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setRiskFilter('all');
    setKraljicFilter('all');
    setDataFilters({
      category: null, category_l2: null, category_l3: null,
      region: null, country: null, sbu: null, fiscal_year: null,
    });
    setCurrentPage(1);
  };

  const filteredSuppliers = useMemo(() => {
    let filtered = suppliers;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.name?.toLowerCase().includes(term) ||
        s.category?.toLowerCase().includes(term) ||
        s.address?.toLowerCase().includes(term) ||
        s.contact_email?.toLowerCase().includes(term) ||
        s.risk_ratings?.toLowerCase().includes(term)
      );
    }

    // Category filter
    if (dataFilters.category) {
      filtered = filtered.filter(s => s.category === dataFilters.category);
    }

    // Risk filter - use risk_ratings field from uploaded data
    if (riskFilter !== 'all') {
      filtered = filtered.filter(s => {
        const riskRating = (s.risk_ratings || '').toLowerCase().trim();
        
        if (riskFilter === 'high') {
          return riskRating === 'high';
        } else if (riskFilter === 'medium') {
          return riskRating === 'medium';
        } else if (riskFilter === 'low') {
          return riskRating === 'low' || riskRating === '';
        }
        return true;
      });
    }

    // Kraljic status filter
    if (kraljicFilter !== 'all') {
      filtered = filtered.filter(s => s.kraljic_status === kraljicFilter);
    }

    // Verification status filter
    if (verificationFilter !== 'all') {
      filtered = filtered.filter(s => (s.verification_status || 'unverified') === verificationFilter);
    }

    // Region filter
    if (dataFilters.region) {
      filtered = filtered.filter(s => s.region === dataFilters.region);
    }

    // Country filter
    if (dataFilters.country) {
      filtered = filtered.filter(s => s.country === dataFilters.country);
    }

    // Sorting - create a new array to ensure React detects the change
    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = sortBy === 'name' ? '' : 0;
      if (bVal === null || bVal === undefined) bVal = sortBy === 'name' ? '' : 0;
      
      // String comparison for name field
      if (sortBy === 'name') {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      // Numeric comparison for other fields
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [suppliers, searchTerm, dataFilters, riskFilter, kraljicFilter, verificationFilter, sortBy, sortOrder]);

  const paginatedSuppliers = useMemo(() => {
    // If showing all, return all filtered suppliers
    if (itemsPerPage >= filteredSuppliers.length) {
      return filteredSuppliers;
    }
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSuppliers.slice(start, start + itemsPerPage);
  }, [filteredSuppliers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage) || 1;

  const activeFilterCount = [
    searchTerm, riskFilter !== 'all' ? riskFilter : null, kraljicFilter !== 'all' ? kraljicFilter : null,
    verificationFilter !== 'all' ? verificationFilter : null,
    ...Object.values(dataFilters),
  ].filter(v => v !== null && v !== '' && v !== 'all').length;

  // Get risk badge based on risk_ratings field (from uploaded data) or risk_score
  const getRiskBadge = (supplier) => {
    const riskRating = (supplier.risk_ratings || '').toLowerCase();
    const riskScore = supplier.risk_score || 0;
    
    // First check the risk_ratings field from uploaded data
    if (riskRating.includes('high')) {
      return { label: 'High Risk', variant: 'destructive', color: 'text-red-500' };
    }
    if (riskRating.includes('medium')) {
      return { label: 'Medium', variant: 'default', color: 'text-yellow-500' };
    }
    if (riskRating.includes('low')) {
      return { label: 'Low Risk', variant: 'secondary', color: 'text-green-500' };
    }
    
    // Fall back to calculated risk_score
    if (riskScore >= 40) return { label: 'High Risk', variant: 'destructive', color: 'text-red-500' };
    if (riskScore >= 20) return { label: 'Medium', variant: 'default', color: 'text-yellow-500' };
    if (riskScore > 0) return { label: 'Low Risk', variant: 'secondary', color: 'text-green-500' };
    
    return { label: 'Not Rated', variant: 'outline', color: 'text-muted-foreground' };
  };

  const viewSupplierDetails = async (supplier) => {
    setSelectedSupplier(supplier);
    try {
      const response = await api.get(`/suppliers/${supplier.id}/scorecard`);
      setScorecard(response.data);
    } catch (error) {
      toast.error('Failed to load supplier details');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-4" data-testid="supplier-directory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Supplier Directory</h1>
          <p className="text-muted-foreground mt-1">
            {filteredSuppliers.length} suppliers found
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
            data-testid="view-grid"
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
            data-testid="view-list"
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Standard Filters via FilterPanel */}
      <FilterPanel
        filters={dataFilters}
        onFiltersChange={(newFilters) => { setDataFilters(newFilters); setCurrentPage(1); }}
        pageName="supplier-directory"
        visibleFilters={['category', 'category_l2', 'category_l3', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
        compact={true}
      />

      {/* Search, Sort & Page-specific Filters */}
      <Card className="border-dashed" data-testid="supplier-directory-filter-panel">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
            {/* Search */}
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, category, location..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
                data-testid="supplier-search"
              />
            </div>

            {/* Risk Filter */}
            <Select value={riskFilter} onValueChange={(v) => { setRiskFilter(v); setCurrentPage(1); }}>
              <SelectTrigger data-testid="filter-risk">
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value="high">High Risk</SelectItem>
                <SelectItem value="medium">Medium Risk</SelectItem>
                <SelectItem value="low">Low Risk</SelectItem>
              </SelectContent>
            </Select>

            {/* Kraljic Filter */}
            <Select value={kraljicFilter} onValueChange={(v) => { setKraljicFilter(v); setCurrentPage(1); }}>
              <SelectTrigger data-testid="filter-kraljic">
                <SelectValue placeholder="Kraljic Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Kraljic</SelectItem>
                <SelectItem value="Strategic">Strategic</SelectItem>
                <SelectItem value="Leverage">Leverage</SelectItem>
                <SelectItem value="Bottleneck">Bottleneck</SelectItem>
                <SelectItem value="Non-Critical">Non-Critical</SelectItem>
              </SelectContent>
            </Select>

            {/* Verification Filter */}
            <Select value={verificationFilter} onValueChange={(v) => { setVerificationFilter(v); setCurrentPage(1); }}>
              <SelectTrigger data-testid="filter-verification">
                <SelectValue placeholder="KYC Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All KYC Status</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="unverified">Unverified</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={`${sortBy}-${sortOrder}`} onValueChange={(v) => {
              const [field, order] = v.split('-');
              setSortBy(field);
              setSortOrder(order);
              setCurrentPage(1);
            }}>
              <SelectTrigger data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="total_spend-desc">Spend (High to Low)</SelectItem>
                <SelectItem value="total_spend-asc">Spend (Low to High)</SelectItem>
                <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                <SelectItem value="transaction_count-desc">Transactions (High to Low)</SelectItem>
                <SelectItem value="transaction_count-asc">Transactions (Low to High)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-primary" />
              <div>
                <p className="text-lg font-bold">{filteredSuppliers.length}</p>
                <p className="text-xs text-muted-foreground">Total Suppliers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-lg font-bold">
                  {cs}{filteredSuppliers.reduce((sum, s) => sum + (s.total_spend || 0), 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Total Spend</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-lg font-bold">
                  {filteredSuppliers.filter(s => (s.risk_ratings || '').toLowerCase().includes('high') || s.risk_score >= 40).length}
                </p>
                <p className="text-xs text-muted-foreground">High Risk</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <Star className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-lg font-bold">
                  {(() => {
                    const suppliersWithScorecard = filteredSuppliers.filter(s => s.has_scorecard && s.scorecard_avg_rating);
                    if (suppliersWithScorecard.length === 0) return 'N/A';
                    const avgRating = suppliersWithScorecard.reduce((sum, s) => sum + s.scorecard_avg_rating, 0) / suppliersWithScorecard.length;
                    return avgRating.toFixed(1);
                  })()}
                </p>
                <p className="text-xs text-muted-foreground">
                  Avg Scorecard Rating
                  {(() => {
                    const count = filteredSuppliers.filter(s => s.has_scorecard).length;
                    return count > 0 ? ` (${count} rated)` : '';
                  })()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-shadow hover:shadow-md ${duplicates?.group_count > 0 ? 'border-amber-300 dark:border-amber-700' : ''}`}
          onClick={() => duplicates?.group_count > 0 && setDuplicateModalOpen(true)}
          data-testid="potential-duplicates-tile"
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <Copy className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-lg font-bold">{duplicates?.group_count ?? '—'}</p>
                <p className="text-xs text-muted-foreground">
                  Potential Duplicates
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Divider between KPIs and supplier tiles */}
      <div className="border-b border-border" />

      {/* Supplier Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paginatedSuppliers.map((supplier, index) => {
            const risk = getRiskBadge(supplier);
            // Limit animation delay to first 12 items for performance
            const animationDelay = index < 12 ? index * 0.05 : 0;
            return (
              <motion.div
                key={supplier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: animationDelay }}
              >
                <Card 
                  className="hover:border-primary hover:shadow-lg transition-all duration-200 cursor-pointer h-full"
                  onClick={() => viewSupplierDetails(supplier)}
                  data-testid="supplier-card"
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={risk.variant}>{risk.label}</Badge>
                        {supplier.kraljic_status && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getKraljicBadge(supplier.kraljic_status).bg} ${getKraljicBadge(supplier.kraljic_status).text}`}>
                            <Target className="w-3 h-3" />
                            {supplier.kraljic_status}
                          </span>
                        )}
                        {(() => {
                          const v = VERIFICATION_CONFIG[supplier.verification_status || 'unverified'];
                          const VIcon = v.icon;
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${v.bg}`}>
                              <VIcon className="w-3 h-3" />
                              {v.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-lg mb-1 line-clamp-1">{supplier.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{supplier.category || 'Uncategorized'}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Spend</span>
                        <span className="font-mono font-semibold">{cs}{supplier.total_spend?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Transactions</span>
                        <span className="font-mono">{supplier.transaction_count || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Scorecard</span>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span className="font-mono">{supplier.scorecard_avg_rating?.toFixed(1) || 'Not rated'}</span>
                        </div>
                      </div>
                      {supplier.governance_score > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Gov. Score</span>
                          <span className={`font-mono font-medium ${supplier.governance_score >= 70 ? 'text-emerald-600' : supplier.governance_score >= 40 ? 'text-amber-600' : 'text-red-500'}`}>{supplier.governance_score}</span>
                        </div>
                      )}
                    </div>

                    {supplier.address && (
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span className="line-clamp-1">{supplier.address}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Supplier</th>
                    <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Category</th>
                    <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Total Spend</th>
                    <th className="text-center py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Risk</th>
                    <th className="text-center py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Kraljic</th>
                    <th className="text-center py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Rating</th>
                    <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Location</th>
                    <th className="text-center py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSuppliers.map((supplier) => {
                    const risk = getRiskBadge(supplier);
                    const kraljic = getKraljicBadge(supplier.kraljic_status);
                    return (
                      <tr 
                        key={supplier.id} 
                        className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => viewSupplierDetails(supplier)}
                        data-testid="supplier-row"
                      >
                        <td className="py-1.5 px-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <Building2 className="w-4 h-4 text-primary" />
                            </div>
                            <span className="font-medium">{supplier.name}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-3 text-muted-foreground">{supplier.category || 'N/A'}</td>
                        <td className="py-1.5 px-3 text-right font-mono font-semibold">{cs}{supplier.total_spend?.toLocaleString()}</td>
                        <td className="py-1.5 px-3 text-center">
                          <Badge variant={risk.variant}>{risk.label}</Badge>
                        </td>
                        <td className="py-1.5 px-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${kraljic.bg} ${kraljic.text}`}>
                            <Target className="w-3 h-3" />
                            {supplier.kraljic_status || 'N/A'}
                          </span>
                        </td>
                        <td className="py-1.5 px-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                            <span>{supplier.scorecard_avg_rating?.toFixed(1) || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-3 text-muted-foreground text-sm">{supplier.address || 'N/A'}</td>
                        <td className="py-1.5 px-3 text-center">
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredSuppliers.length)} to {Math.min(currentPage * itemsPerPage, filteredSuppliers.length)} of {filteredSuppliers.length}
          </p>
          <Select 
            value={itemsPerPage.toString()} 
            onValueChange={(v) => { 
              setItemsPerPage(v === 'all' ? filteredSuppliers.length : parseInt(v)); 
              setCurrentPage(1); 
            }}
          >
            <SelectTrigger className="w-[140px]" data-testid="items-per-page">
              <SelectValue placeholder="Per page" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="12">12 per page</SelectItem>
              <SelectItem value="24">24 per page</SelectItem>
              <SelectItem value="48">48 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
              <SelectItem value="all">Show All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </div>
        )}
      </div>

      {/* Supplier Detail Modal/Panel */}
      {selectedSupplier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedSupplier(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="supplier-detail-modal"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold">{selectedSupplier.name}</h2>
                  <p className="text-muted-foreground">{selectedSupplier.category || 'Uncategorized'}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedSupplier(null)}>
                  ✕
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Spend</p>
                    <p className="text-lg font-bold font-mono">{cs}{selectedSupplier.total_spend?.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Risk Score</p>
                    <p className="text-lg font-bold font-mono">{selectedSupplier.risk_score?.toFixed(0) || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {scorecard && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Performance Scorecard</h3>
                  
                  {/* Main Score */}
                  <div className="p-4 bg-primary/10 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Overall Performance Score</p>
                    <p className="text-xl font-bold">{scorecard.performance_score?.toFixed(1)}/100</p>
                    {scorecard.has_scorecard && (
                      <p className="text-xs text-green-600 mt-1">Based on rated scorecard</p>
                    )}
                  </div>
                  
                  {/* Scorecard Details */}
                  {scorecard.scorecard_details && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Quality Score</p>
                        <p className="text-lg font-bold">{scorecard.scorecard_details.quality_score ?? 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Delivery Score</p>
                        <p className="text-lg font-bold">{scorecard.scorecard_details.delivery_score ?? 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Price Score</p>
                        <p className="text-lg font-bold">{scorecard.scorecard_details.price_score ?? 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Compliance Score</p>
                        <p className="text-lg font-bold">{scorecard.scorecard_details.compliance_score ?? 'N/A'}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Transaction Stats */}
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Transaction Count</p>
                      <p className="text-lg font-bold">{scorecard.transaction_count}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Avg Transaction</p>
                      <p className="text-lg font-bold">{cs}{scorecard.avg_transaction_value?.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Contract Compliance</p>
                      <p className="text-lg font-bold">{scorecard.contract_compliance_rate?.toFixed(1)}%</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Evaluated Risk</p>
                      <p className="text-lg font-bold">{scorecard.evaluated_risk || 'Not Evaluated'}</p>
                    </div>
                  </div>

                  {scorecard.recommendations && (
                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Recommendations</h4>
                      <ul className="space-y-2">
                        {scorecard.recommendations.map((rec, idx) => (
                          <li key={`${idx}-${String(rec).slice(0, 40)}`} className="flex items-start gap-2 text-sm">
                            <span className="text-primary">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 pt-6 border-t space-y-3">
                {/* KYC Verification Section */}
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> KYC Verification & Certifications
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Verification Status</p>
                    {(() => {
                      const v = VERIFICATION_CONFIG[selectedSupplier.verification_status || 'unverified'];
                      const VIcon = v.icon;
                      return (
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${v.bg}`}>
                            <VIcon className="w-3 h-3" />
                            {v.label}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Governance Score</p>
                    <p className={`text-lg font-bold ${(selectedSupplier.governance_score || 0) >= 70 ? 'text-emerald-600' : (selectedSupplier.governance_score || 0) >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                      {selectedSupplier.governance_score || 0}/100
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-xs" data-testid="manage-verification-btn"
                    onClick={(e) => { e.stopPropagation(); setVerificationModalSupplier(selectedSupplier); setVerifyStatus(selectedSupplier.verification_status || 'unverified'); setVerifyNotes(''); }}>
                    <ShieldCheck className="w-3 h-3 mr-1" /> Manage Verification
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs" data-testid="manage-certs-btn"
                    onClick={async (e) => {
                      e.stopPropagation();
                      setCertModalSupplier(selectedSupplier);
                      try {
                        const res = await api.get(`/suppliers/${selectedSupplier.id}/certifications`);
                        setCerts(res.data.certifications || []);
                      } catch { setCerts([]); }
                    }}>
                    <FileCheck className="w-3 h-3 mr-1" /> Certifications ({(selectedSupplier.certifications || []).length})
                  </Button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t space-y-3">
                {selectedSupplier.contact_email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedSupplier.contact_email}</span>
                  </div>
                )}
                {selectedSupplier.contact_phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedSupplier.contact_phone}</span>
                  </div>
                )}
                {selectedSupplier.address && (
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedSupplier.address}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Verification Management Modal */}
      <Dialog open={!!verificationModalSupplier} onOpenChange={(open) => { if (!open) setVerificationModalSupplier(null); }}>
        <DialogContent className="max-w-md" data-testid="verification-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="w-5 h-5" /> KYC Verification
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{verificationModalSupplier?.name}</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={verifyStatus} onValueChange={setVerifyStatus}>
                <SelectTrigger data-testid="verification-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unverified">Unverified</SelectItem>
                  <SelectItem value="pending">Pending Review</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Notes</label>
              <Input
                placeholder="KYC review notes..."
                value={verifyNotes}
                onChange={(e) => setVerifyNotes(e.target.value)}
                data-testid="verification-notes"
              />
            </div>
            <Button className="w-full" disabled={verifyLoading} data-testid="save-verification-btn"
              onClick={async () => {
                setVerifyLoading(true);
                try {
                  await api.put(`/suppliers/${verificationModalSupplier.id}/verification`, {
                    status: verifyStatus, notes: verifyNotes,
                  });
                  toast.success(`Verification status updated to "${verifyStatus}"`);
                  setVerificationModalSupplier(null);
                  fetchSuppliers();
                } catch (err) {
                  toast.error('Failed to update verification');
                } finally { setVerifyLoading(false); }
              }}>
              {verifyLoading ? 'Saving...' : 'Save Verification'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Certification Management Modal */}
      <Dialog open={!!certModalSupplier} onOpenChange={(open) => { if (!open) setCertModalSupplier(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="certification-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileCheck className="w-5 h-5" /> Certifications
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{certModalSupplier?.name}</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Existing Certifications */}
            {certs.length > 0 ? (
              <div className="space-y-2">
                {certs.map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                    <div>
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {c.issuer && `${c.issuer} · `}
                        {c.issue_date && `Issued: ${c.issue_date}`}
                        {c.expiry_date && ` · Expires: ${c.expiry_date}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] ${c.status === 'valid' ? 'bg-emerald-100 text-emerald-800' : c.status === 'expiring' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                        {c.status}
                      </Badge>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid={`delete-cert-${c.id}`}
                        onClick={async () => {
                          try {
                            await api.delete(`/suppliers/${certModalSupplier.id}/certifications/${c.id}`);
                            setCerts(prev => prev.filter(x => x.id !== c.id));
                            toast.success('Certification removed');
                            fetchSuppliers();
                          } catch { toast.error('Failed to delete certification'); }
                        }}>
                        <Trash2 className="w-3 h-3 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No certifications recorded</p>
            )}

            {/* Add New Certification */}
            <div className="border-t pt-3 space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground">Add Certification</h4>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Certification name (e.g. ISO 9001)" value={newCert.name}
                  onChange={(e) => setNewCert(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="cert-name-input" />
                <Input placeholder="Issuer" value={newCert.issuer}
                  onChange={(e) => setNewCert(prev => ({ ...prev, issuer: e.target.value }))}
                  data-testid="cert-issuer-input" />
                <Input type="date" placeholder="Issue Date" value={newCert.issue_date}
                  onChange={(e) => setNewCert(prev => ({ ...prev, issue_date: e.target.value }))}
                  data-testid="cert-issue-date" />
                <Input type="date" placeholder="Expiry Date" value={newCert.expiry_date}
                  onChange={(e) => setNewCert(prev => ({ ...prev, expiry_date: e.target.value }))}
                  data-testid="cert-expiry-date" />
              </div>
              <Button size="sm" className="w-full" disabled={!newCert.name.trim()} data-testid="add-cert-btn"
                onClick={async () => {
                  try {
                    const res = await api.post(`/suppliers/${certModalSupplier.id}/certifications`, newCert);
                    setCerts(prev => [...prev, { ...res.data.certification, status: 'valid' }]);
                    setNewCert({ name: '', issuer: '', issue_date: '', expiry_date: '' });
                    toast.success('Certification added');
                    fetchSuppliers();
                  } catch { toast.error('Failed to add certification'); }
                }}>
                <Plus className="w-3 h-3 mr-1" /> Add Certification
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Resolution Modal */}
      <Dialog open={duplicateModalOpen} onOpenChange={setDuplicateModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="duplicate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5" /> Normalize Duplicate Suppliers
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Select a master record for each group, then merge to consolidate transactions under one supplier name.
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {duplicates?.groups?.map((group, gi) => (
              <DuplicateGroup
                key={gi}
                group={group}
                index={gi}
                onNormalize={normalizeGroup}
                normalizing={normalizing}
                currencySymbol={cs}
              />
            ))}
            {(!duplicates?.groups || duplicates.groups.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-8">No duplicate groups detected.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
